import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Circle, FileText, Hourglass, Images, Lightbulb,
  ListChecks, Loader2, MessageSquareText, Sparkles, Timer, XCircle,
} from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { api, COPY_FIELDS, type CopyField, type CopyTexts } from '@/lib/api';
import { classNames } from '@/lib/utils';
import { ETIQUETAS_CAMPO, IDIOMAS, type OpcionesIA } from '@/components/copy/tipos';

/**
 * Flujo "Generar todo con IA": los cuatro pasos de la app original
 * (aviso → opciones → progreso → previsualización) en un único modal.
 *
 * Este componente NUNCA guarda: entrega los textos aceptados a `onAplicar` y
 * el padre decide dónde van (idioma base o traducción). Tampoco toca el
 * nickname del anuncio —solo mueve los 14 campos de `COPY_FIELDS`—, que es la
 * regla intocable que traía la app original.
 */

interface Props {
  abierto: boolean;
  anuncioId: number;
  ficha: any;                 // respuesta de api.getCopyListing (trae photos, amenities)
  idioma: string;             // idioma en el que se genera
  textosActuales: CopyTexts;  // para la comparación lado a lado
  numFotos: number;
  onCerrar: () => void;
  onAplicar: (textos: CopyTexts) => Promise<void>;
}

type Paso = 'aviso' | 'opciones' | 'progreso' | 'previsualizacion';
type EstadoCampo = 'pendiente' | 'girando' | 'hecho' | 'error';

/** Clave de localStorage del "No volver a mostrar" del aviso de duración. */
const CLAVE_AVISO = 'copyAvisoIaOculto';

/**
 * Siete llamadas a la vez, como en la app original. Los 14 campos en serie
 * tardaban minutos; con más de siete el proveedor empieza a devolver 429.
 */
const CONCURRENCIA = 7;

/** El servidor recorta el lote de fotos que manda al modelo. */
const MAX_FOTOS_SERVIDOR = 8;

function estadosIniciales(): Record<CopyField, EstadoCampo> {
  const mapa = {} as Record<CopyField, EstadoCampo>;
  for (const campo of COPY_FIELDS) mapa[campo] = 'pendiente';
  return mapa;
}

function formatearTiempo(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Alto razonable para un textarea según lo que trae dentro. */
function filasPara(texto: string): number {
  const lineas = texto.split('\n').length + Math.floor(texto.length / 75);
  return Math.min(16, Math.max(4, lineas));
}

export default function GenerarTodoFlujo({
  abierto, anuncioId, ficha, idioma, textosActuales, numFotos, onCerrar, onAplicar,
}: Props) {
  const [paso, setPaso] = useState<Paso>('aviso');

  // Paso 1
  const [noMostrarAviso, setNoMostrarAviso] = useState(false);

  // Paso 2
  const [opciones, setOpciones] = useState<OpcionesIA>({
    incluirAmenities: true, incluirFotos: true, instrucciones: '',
  });

  // Paso 3
  const [estados, setEstados] = useState<Record<CopyField, EstadoCampo>>(estadosIniciales);
  const [tiempos, setTiempos] = useState<Partial<Record<CopyField, number>>>({});
  const [completados, setCompletados] = useState(0);
  const [transcurrido, setTranscurrido] = useState(0);
  const [cancelando, setCancelando] = useState(false);

  // Paso 4
  const [sugerencias, setSugerencias] = useState<CopyTexts>({});
  const [titulos, setTitulos] = useState<string[]>([]);
  const [aplicar, setAplicar] = useState<Partial<Record<CopyField, boolean>>>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancelarRef = useRef(false);
  const inicioRef = useRef(0);
  const tiemposRef = useRef<number[]>([]);

  // Lo que el bucle de generación necesita leer sin que un cambio de props
  // reinicie el flujo a medias.
  const numFotosRef = useRef(numFotos);
  numFotosRef.current = numFotos;
  const textosRef = useRef(textosActuales);
  textosRef.current = textosActuales;

  // Cada apertura empieza de cero: el paso inicial depende de si el aviso de
  // duración ya se descartó alguna vez.
  useEffect(() => {
    if (!abierto) return;
    cancelarRef.current = false;
    tiemposRef.current = [];
    setEstados(estadosIniciales());
    setTiempos({});
    setCompletados(0);
    setTranscurrido(0);
    setCancelando(false);
    setSugerencias({});
    setTitulos([]);
    setAplicar({});
    setGuardando(false);
    setError(null);
    setNoMostrarAviso(false);
    setOpciones({
      incluirAmenities: true,
      // Sin fotos no hay nada que mandar, así que el interruptor nace apagado.
      incluirFotos: numFotosRef.current > 0,
      instrucciones: '',
    });

    let oculto = false;
    // Safari en navegación privada lanza al tocar localStorage.
    try { oculto = localStorage.getItem(CLAVE_AVISO) === '1'; } catch { /* sin persistencia */ }
    setPaso(oculto ? 'opciones' : 'aviso');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  // Cronómetro del paso 3. Cada tick también refresca la ETA, que se calcula
  // en el render a partir de los tiempos ya medidos.
  useEffect(() => {
    if (paso !== 'progreso') return;
    const id = window.setInterval(() => {
      setTranscurrido(Math.floor((Date.now() - inicioRef.current) / 1000));
    }, 500);
    return () => window.clearInterval(id);
  }, [paso]);

  const total = COPY_FIELDS.length;
  const idiomaEtiqueta = IDIOMAS.find((i) => i.code === idioma)?.label || idioma;
  const fotosAEnviar = Math.min(numFotos, MAX_FOTOS_SERVIDOR);
  const numAmenities = Array.isArray(ficha?.amenities) ? ficha.amenities.length : 0;

  const media = tiemposRef.current.length
    ? tiemposRef.current.reduce((a, b) => a + b, 0) / tiemposRef.current.length
    : 0;
  const eta = completados > 0 && completados < total
    ? Math.ceil((media * (total - completados)) / 1000)
    : null;

  // ── Paso 1 ────────────────────────────────────────────────────────────
  const continuarDesdeAviso = () => {
    if (noMostrarAviso) {
      try { localStorage.setItem(CLAVE_AVISO, '1'); } catch { /* sin persistencia */ }
    }
    setPaso('opciones');
  };

  // ── Paso 3: pool de generación ────────────────────────────────────────
  const lanzarGeneracion = async () => {
    cancelarRef.current = false;
    tiemposRef.current = [];
    inicioRef.current = Date.now();
    setEstados(estadosIniciales());
    setTiempos({});
    setCompletados(0);
    setTranscurrido(0);
    setCancelando(false);
    setPaso('progreso');

    const campos = [...COPY_FIELDS];
    const generados: CopyTexts = {};
    let propuestas: string[] = [];
    const extra = opciones.instrucciones.trim();
    let siguiente = 0;

    const marcar = (campo: CopyField, estado: EstadoCampo) => {
      setEstados((prev) => { const sig = { ...prev }; sig[campo] = estado; return sig; });
    };

    /**
     * Cada trabajador va cogiendo el siguiente índice libre. `siguiente++` no
     * se interrumpe entre lectura y escritura —el bucle de eventos es de un
     * solo hilo—, así que ningún campo se genera dos veces ni se salta.
     */
    const trabajador = async () => {
      while (siguiente < campos.length) {
        if (cancelarRef.current) return;
        const campo = campos[siguiente++];
        marcar(campo, 'girando');
        const t0 = Date.now();

        try {
          if (campo === 'name') {
            // El título no sale del generador de campos: pide cinco propuestas
            // para que luego se elija una en la previsualización.
            const res = await api.generateCopyTitles(anuncioId, {
              language: idioma,
              ...(extra ? { suggestions: extra } : {}),
            });
            const lista = res.titles || [];
            if (lista.length > 0) {
              propuestas = lista;
              generados.name = lista[0];
              marcar('name', 'hecho');
            } else {
              marcar('name', 'error');
            }
          } else {
            const res = await api.generateCopyField(anuncioId, {
              field: campo,
              language: idioma,
              current_value: textosRef.current[campo] || '',
              include_amenities: opciones.incluirAmenities,
              include_photos: opciones.incluirFotos,
              ...(extra ? { extra_context: extra } : {}),
            });
            if (res.success && res.text) {
              generados[campo] = res.text;
              marcar(campo, 'hecho');
            } else {
              marcar(campo, 'error');
            }
          }
        } catch {
          // Un campo que falla no aborta la tanda: los otros trece siguen.
          marcar(campo, 'error');
        }

        const ms = Date.now() - t0;
        tiemposRef.current.push(ms);
        setTiempos((prev) => { const sig = { ...prev }; sig[campo] = ms; return sig; });
        setCompletados((n) => n + 1);
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCIA }, () => trabajador()));

    const claves = Object.keys(generados) as CopyField[];
    if (claves.length === 0) {
      // Cancelar al principio no merece aviso; que fallen los catorce, sí.
      if (!cancelarRef.current) {
        alert('La IA no devolvió ningún texto. Revisa la conexión con el servidor e inténtalo de nuevo.');
      }
      onCerrar();
      return;
    }

    const inicial: Partial<Record<CopyField, boolean>> = {};
    for (const campo of claves) inicial[campo] = true;

    setSugerencias(generados);
    setTitulos(propuestas);
    setAplicar(inicial);
    // Con resultados parciales tras cancelar también se pasa a revisarlos.
    setPaso('previsualizacion');
  };

  const cancelarGeneracion = () => {
    cancelarRef.current = true;
    setCancelando(true);
  };

  // ── Paso 4 ────────────────────────────────────────────────────────────
  const editarSugerencia = (campo: CopyField, valor: string) => {
    setSugerencias((prev) => { const sig = { ...prev }; sig[campo] = valor; return sig; });
  };

  const alternarAplicar = (campo: CopyField, valor: boolean) => {
    setAplicar((prev) => { const sig = { ...prev }; sig[campo] = valor; return sig; });
  };

  const aceptarTodo = async () => {
    const textos: CopyTexts = {};
    for (const campo of COPY_FIELDS) {
      const valor = sugerencias[campo];
      if (valor === undefined) continue;         // no se generó
      if (aplicar[campo] === false) continue;    // interruptor apagado
      textos[campo] = valor;
    }

    if (Object.keys(textos).length === 0) {
      setError('No hay ningún campo marcado para aplicar.');
      return;
    }

    setGuardando(true);
    setError(null);
    try {
      await onAplicar(textos);
      onCerrar();
    } catch (e: any) {
      setError(e?.message || 'No se pudieron guardar los textos.');
    } finally {
      setGuardando(false);
    }
  };

  const descartar = () => {
    if (!confirm('¿Descartar las sugerencias? Se perderá todo lo que ha generado la IA.')) return;
    onCerrar();
  };

  // La X y el fondo del modal hacen lo mismo que el botón del pie de cada paso.
  const alCerrarModal = () => {
    if (paso === 'progreso') { cancelarGeneracion(); return; }
    if (paso === 'previsualizacion') { descartar(); return; }
    onCerrar();
  };

  const titulo = paso === 'aviso' ? 'Generar todo con IA'
    : paso === 'opciones' ? 'Opciones de generación'
    : paso === 'progreso' ? 'Generando textos con IA'
    : 'Sugerencias de la IA';

  const tamano = paso === 'aviso' ? 'sm' : paso === 'opciones' ? 'md' : paso === 'progreso' ? 'lg' : '2xl';

  const camposGenerados = COPY_FIELDS.filter((c) => sugerencias[c] !== undefined);
  const camposConError = COPY_FIELDS.filter((c) => estados[c] === 'error');

  return (
    <Modal isOpen={abierto} onClose={alCerrarModal} title={titulo} size={tamano}>
      {/* ── Paso 1: aviso de duración ──────────────────────────────── */}
      {paso === 'aviso' && (
        <div>
          <div className="flex gap-3">
            <Hourglass size={20} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-sereno-dark/70">
              La generación de los 14 textos puede tardar varios minutos. No cierres
              la pestaña mientras se completa.
            </p>
          </div>

          <label className="flex items-center gap-2 mt-5 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={noMostrarAviso}
              onChange={(e) => setNoMostrarAviso(e.target.checked)}
              className="w-4 h-4 accent-sereno-dark cursor-pointer"
            />
            <span className="text-sereno-dark">No volver a mostrar este mensaje</span>
          </label>

          <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-gray-100">
            <button type="button" onClick={onCerrar} className="btn-ghost">Cancelar</button>
            <button type="button" onClick={continuarDesdeAviso} className="btn-primary flex items-center gap-2">
              <Sparkles size={16} /> Continuar
            </button>
          </div>
        </div>
      )}

      {/* ── Paso 2: opciones de enriquecimiento ────────────────────── */}
      {paso === 'opciones' && (
        <div>
          <p className="text-sm text-sereno-dark/60 mb-5">
            Elige qué información adicional del anuncio se envía al modelo para
            enriquecer los textos. Se generarán en <span className="font-medium text-sereno-dark">{idiomaEtiqueta}</span>.
          </p>

          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={opciones.incluirAmenities}
                onChange={(e) => setOpciones((o) => ({ ...o, incluirAmenities: e.target.checked }))}
                className="w-4 h-4 mt-0.5 accent-sereno-dark cursor-pointer"
              />
              <span className="text-sm">
                <span className="flex items-center gap-1.5 font-medium text-sereno-dark">
                  <ListChecks size={15} /> Amenities
                </span>
                <span className="block text-xs text-sereno-dark/50 mt-0.5">
                  Manda la lista de servicios y equipamiento del anuncio
                  {numAmenities > 0 ? ` (${numAmenities} en la ficha)` : ''}.
                </span>
              </span>
            </label>

            <label className={classNames(
              'flex items-start gap-3',
              numFotos === 0 ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
            )}>
              <input
                type="checkbox"
                checked={opciones.incluirFotos}
                disabled={numFotos === 0}
                onChange={(e) => setOpciones((o) => ({ ...o, incluirFotos: e.target.checked }))}
                className="w-4 h-4 mt-0.5 accent-sereno-dark cursor-pointer disabled:cursor-not-allowed"
              />
              <span className="text-sm">
                <span className="flex items-center gap-1.5 font-medium text-sereno-dark">
                  <Images size={15} /> Fotos del anuncio
                </span>
                <span className="block text-xs text-sereno-dark/50 mt-0.5">
                  El modelo verá las fotos para describir mejor el espacio.
                </span>
                {numFotos === 0 ? (
                  <span className="block text-xs text-sereno-dark/40 mt-1">
                    Este anuncio no tiene fotos en Hostify.
                  </span>
                ) : opciones.incluirFotos && (
                  <span className="block text-xs text-blue-700 mt-1">
                    Se enviarán {fotosAEnviar} de las {numFotos} fotos como contexto visual.
                  </span>
                )}
              </span>
            </label>

            <div>
              <label className="label flex items-center gap-1.5">
                <MessageSquareText size={15} /> Instrucciones adicionales
                <span className="font-normal text-sereno-dark/40">(opcional)</span>
              </label>
              <textarea
                className="input"
                rows={3}
                value={opciones.instrucciones}
                onChange={(e) => setOpciones((o) => ({ ...o, instrucciones: e.target.value }))}
                placeholder="Ej: destaca que tiene vistas al mar, menciona que está recién reformado…"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-gray-100">
            <button type="button" onClick={onCerrar} className="btn-ghost">Cancelar</button>
            <button type="button" onClick={lanzarGeneracion} className="btn-primary flex items-center gap-2">
              <Sparkles size={16} /> Generar con IA
            </button>
          </div>
        </div>
      )}

      {/* ── Paso 3: progreso ───────────────────────────────────────── */}
      {paso === 'progreso' && (
        <div>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <p className="text-sm font-medium text-sereno-dark">
                {completados} / {total} campos
              </p>
              <span className="badge bg-blue-100 text-blue-700 mt-1.5">
                {idioma.toUpperCase()} · {idiomaEtiqueta}
              </span>
            </div>
            <div className="text-right shrink-0">
              <p className="flex items-center justify-end gap-1.5 text-sm text-sereno-dark/60 tabular-nums">
                <Timer size={14} /> {formatearTiempo(transcurrido)}
              </p>
              {eta !== null && (
                <p className="text-xs text-sereno-dark/40 tabular-nums mt-0.5">
                  ~{formatearTiempo(eta)} restantes
                </p>
              )}
            </div>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mb-4">
            <div
              className="bg-sereno-dark h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.round((completados / total) * 100)}%` }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 max-h-80 overflow-y-auto">
            {COPY_FIELDS.map((campo) => {
              const estado = estados[campo];
              const ms = tiempos[campo];
              return (
                <div
                  key={campo}
                  className={classNames(
                    'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm',
                    estado === 'girando' && 'bg-sereno-blue/10',
                  )}
                >
                  {estado === 'girando' && <Loader2 size={15} className="animate-spin text-sereno-dark/60 shrink-0" />}
                  {estado === 'hecho' && <CheckCircle2 size={15} className="text-green-600 shrink-0" />}
                  {estado === 'error' && <XCircle size={15} className="text-red-500 shrink-0" />}
                  {estado === 'pendiente' && <Circle size={15} className="text-sereno-dark/25 shrink-0" />}
                  <span className={classNames(
                    'flex-1 truncate',
                    estado === 'pendiente' ? 'text-sereno-dark/40' : 'text-sereno-dark/80',
                  )}>
                    {ETIQUETAS_CAMPO[campo]}
                  </span>
                  {estado === 'hecho' && ms !== undefined && (
                    <span className="text-xs tabular-nums text-sereno-dark/40 shrink-0">
                      {formatearTiempo(Math.round(ms / 1000))}
                    </span>
                  )}
                  {estado === 'error' && (
                    <span className="text-xs text-red-500 shrink-0">Error</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end pt-5 mt-5 border-t border-gray-100">
            <button
              type="button"
              onClick={cancelarGeneracion}
              disabled={cancelando}
              className="btn-ghost text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelando ? 'Cancelando…' : 'Cancelar'}
            </button>
          </div>
        </div>
      )}

      {/* ── Paso 4: previsualización lado a lado ───────────────────── */}
      {paso === 'previsualizacion' && (
        <div>
          <p className="text-sm text-sereno-dark/60 mb-4">
            Revisa y edita los textos generados en <span className="font-medium text-sereno-dark">{idiomaEtiqueta}</span>.
            Apaga el interruptor de los que no quieras aplicar.
          </p>

          {camposConError.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 mb-4">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>
                {camposConError.length === 1 ? 'No se pudo generar ' : 'No se pudieron generar '}
                {camposConError.map((c) => ETIQUETAS_CAMPO[c]).join(', ')}. Esos campos
                se quedan como están.
              </span>
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-rose-600 text-sm px-3 py-2 mb-4">
              {error}
            </div>
          )}

          <div className="space-y-5">
            {camposGenerados.map((campo) => {
              const actual = (textosActuales[campo] || '').trim();
              const sugerido = (sugerencias[campo] || '').trim();
              const cambiado = sugerido !== actual;
              const activo = aplicar[campo] !== false;
              // Solo merece la pena la columna de la izquierda si hay un texto
              // anterior distinto con el que comparar.
              const conComparacion = cambiado && !!actual;

              return (
                <div
                  key={campo}
                  className={classNames(
                    'rounded-xl border border-gray-200 p-4 transition-opacity',
                    !activo && 'opacity-50',
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-sereno-dark">{ETIQUETAS_CAMPO[campo]}</span>
                    {cambiado && actual && <span className="badge bg-blue-100 text-blue-700">Modificado</span>}
                    {!actual && sugerido && <span className="badge bg-green-100 text-green-700">Nuevo</span>}
                  </div>

                  {campo === 'name' && titulos.length > 0 ? (
                    <div className={classNames('grid gap-4', conComparacion && 'md:grid-cols-2')}>
                      {conComparacion && (
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-medium text-sereno-dark/50 mb-1">
                            <FileText size={13} /> Título actual
                          </p>
                          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-sereno-dark/70">
                            {textosActuales.name}
                          </div>
                        </div>
                      )}
                      <div>
                        <p className="flex items-center gap-1.5 text-xs font-medium text-green-700 mb-1">
                          <Lightbulb size={13} /> Elige un título
                        </p>
                        <div className="space-y-1.5">
                          {titulos.map((t, i) => {
                            const elegido = sugerencias.name === t;
                            return (
                              <label
                                key={i}
                                className={classNames(
                                  'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer',
                                  elegido ? 'border-gray-300 bg-sereno-blue/50' : 'border-gray-200 hover:bg-gray-50',
                                )}
                              >
                                <input
                                  type="radio"
                                  name="copy-titulo-ia"
                                  checked={elegido}
                                  onChange={() => editarSugerencia('name', t)}
                                  className="w-4 h-4 mt-0.5 accent-sereno-dark cursor-pointer shrink-0"
                                />
                                <span className="flex-1 text-sereno-dark">
                                  {t}
                                  {/* El título del anuncio no puede pasar de 50 caracteres. */}
                                  <span className={classNames(
                                    'ml-2 text-xs tabular-nums',
                                    t.length > 50 ? 'text-rose-600' : 'text-sereno-dark/40',
                                  )}>
                                    {t.length}/50
                                  </span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={classNames('grid gap-4', conComparacion && 'md:grid-cols-2')}>
                      {conComparacion && (
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-medium text-sereno-dark/50 mb-1">
                            <FileText size={13} /> Texto actual
                          </p>
                          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-sereno-dark/70 whitespace-pre-wrap max-h-72 overflow-y-auto">
                            {textosActuales[campo]}
                          </div>
                        </div>
                      )}
                      <div>
                        <p className="flex items-center gap-1.5 text-xs font-medium text-green-700 mb-1">
                          <Sparkles size={13} /> Sugerencia IA
                        </p>
                        <textarea
                          className="input text-sm"
                          rows={filasPara(sugerencias[campo] || '')}
                          maxLength={campo === 'name' ? 50 : undefined}
                          value={sugerencias[campo] || ''}
                          onChange={(e) => editarSugerencia(campo, e.target.value)}
                        />
                        {campo === 'name' && (
                          <p className={classNames(
                            'text-xs tabular-nums mt-1',
                            (sugerencias.name || '').length >= 50 ? 'text-rose-600' : 'text-sereno-dark/40',
                          )}>
                            {(sugerencias.name || '').length}/50
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={activo}
                      onChange={(e) => alternarAplicar(campo, e.target.checked)}
                      className="w-4 h-4 accent-sereno-dark cursor-pointer"
                    />
                    <span className="text-sereno-dark/70">Aplicar este texto</span>
                  </label>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-gray-100">
            <button type="button" onClick={descartar} disabled={guardando} className="btn-ghost">
              Descartar
            </button>
            <button
              type="button"
              onClick={aceptarTodo}
              disabled={guardando}
              className="btn-primary flex items-center gap-2"
            >
              {guardando ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {guardando ? 'Guardando…' : 'Aceptar e insertar todo'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
